import { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'
import { AxiosError } from 'axios'
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { AuthProvider, useAuth } from '@/providers/auth-provider'
import { PostHogProvider } from '@posthog/react'
import 'react-medium-image-zoom/dist/styles.css'
import { toast } from 'sonner'
import { reportError } from '@/lib/error-reporter'
import { RecordNotFoundError } from '@/lib/errors'
import { initializeSentry } from '@/lib/sentry'
import { signOut as signOutLib } from '@/lib/supabase/auth-helpers'
import { PageSpinner } from '@/components/ui/spinner'
import { APP_SCROLL_ID } from '@/components/layout/app-scroll'
import { ThemeProvider } from './context/theme-provider'
import { GeneralError } from './features/errors/general-error'
import { NotFoundError } from './features/errors/not-found-error'
// Generated Routes
import { routeTree } from './routeTree.gen'
// Styles
import './styles/index.css'

// Initialize Sentry as early as possible
initializeSentry()

// Entry file - components are intentionally not exported for Fast Refresh
/* eslint-disable react-refresh/only-export-components */

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (error instanceof RecordNotFoundError) {
          return false
        }

        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.log({ failureCount, error })
        }

        if (failureCount >= 0 && import.meta.env.DEV) {
          return false
        }
        if (failureCount > 3 && import.meta.env.PROD) {
          return false
        }

        return !(
          error instanceof AxiosError &&
          [401, 403].includes(error.response?.status ?? 0)
        )
      },
      refetchOnWindowFocus: import.meta.env.PROD,
      staleTime: 10 * 1000, // 10s
    },
  },
  queryCache: new QueryCache({
    onError: (error) => {
      // Expected zero-row outcome — route guards own the redirect
      if (error instanceof RecordNotFoundError) {
        return
      }

      // Check for Axios errors from custom APIs
      if (error instanceof AxiosError) {
        const status = error.response?.status

        if (status === 401) {
          toast.error('Session expired!')
          signOutLib().catch(() => {})
          window.location.href = '/sign-in'
          return
        }

        if (status === 403) {
          // Permission denied - normal authorization flow, no need to report as error
          return
        }

        if (status === 500) {
          reportError(error, { toast: true })
        }
      }

      // Report to Sentry but don't toast — suspense error boundaries
      // (e.g. IrisErrorBoundary) already render a user-friendly screen,
      // and raw `error.message` strings are developer-facing. Mirrors the
      // mutationCache pattern below where local handlers own UI copy.
      reportError(error, { toast: false })
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      // Handle session expiry for Axios errors
      if (error instanceof AxiosError && error.response?.status === 401) {
        toast.error('Session expired!')
        signOutLib().catch(() => {})
        window.location.href = '/sign-in'
        return
      }

      // Report all mutation errors to Sentry (no toast - local handlers show contextual messages)
      reportError(error, { toast: false })
    },
  }),
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createRouter>
  }
}

// Create router once (context will be set after AuthProvider wraps app)
const router = createRouter({
  routeTree,
  context: {
    queryClient,
    auth: undefined!, // This will be set after we wrap the app in an AuthProvider
  },
  defaultPreload: 'render',
  defaultPreloadStaleTime: 0, // delegate cache management to React Query
  defaultPendingComponent: PageSpinner,
  defaultPendingMs: 300,
  defaultPendingMinMs: 500,
  defaultNotFoundComponent: NotFoundError,
  defaultErrorComponent: GeneralError,
  scrollRestoration: true,
  // The app frame scrolls, not the window: reset it on navigation too.
  scrollToTopSelectors: [`#${APP_SCROLL_ID}`],
})

// App component with auth provider
function InnerApp() {
  const auth = useAuth()

  // Wait for auth to load before rendering router
  if (auth.isLoading) {
    return <PageSpinner />
  }

  // Pass current auth state to router via context prop
  return <RouterProvider router={router} context={{ queryClient, auth }} />
}

const posthogToken = import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN
const posthogUiHost =
  import.meta.env.VITE_PUBLIC_POSTHOG_HOST || 'https://eu.posthog.com'

function App() {
  const app = (
    <AuthProvider queryClient={queryClient}>
      <ThemeProvider>
        <InnerApp />
      </ThemeProvider>
    </AuthProvider>
  )

  if (!posthogToken) {
    return app
  }

  return (
    <PostHogProvider
      apiKey={posthogToken}
      options={{
        api_host: '/ingest',
        ui_host: posthogUiHost,
        defaults: '2026-01-30',
        capture_exceptions: true,
        debug: import.meta.env.DEV,
        disable_session_recording: false,
        session_recording: {
          maskAllInputs: true,
        },
      }}
    >
      {app}
    </PostHogProvider>
  )
}

// Render the app
const rootElement = document.getElementById('root')!
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement)
  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </StrictMode>
  )
}
